import React, { useContext, useEffect, useCallback } from "react";
import Checkout from "./components/Checkout/Checkout";
import "react-notifications/lib/notifications.css";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import OrderQueue from "./components/OrderQueue/OrderQueue";
import { withFirebase } from "./components/Firebase";
import NetworkLockedIcon from "@material-ui/icons/NetworkLocked";
import NotificationsActiveIcon from "@material-ui/icons/NotificationsActive";
import NotificationsOffIcon from "@material-ui/icons/NotificationsOff";
import { AuthUserContext } from "./components/Session";
import { Box, IconButton, Tooltip } from "@material-ui/core";
import OrderRepository from "./repository/OrderRepository";
import ProductRepository from "./repository/ProductRepository";
import { NotificationManager } from "react-notifications";

const drawerWidth = 380;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  appBar: {
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    width: `calc(100% - ${drawerWidth}px)`,
    marginRight: drawerWidth,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginLeft: theme.spacing(2),
  },
  hide: {
    display: "none",
  },
  drawer: {
    width: 0,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
  },
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-end",
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: 0,
  },
  contentShift: {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.up("sm")]: {
      marginRight: drawerWidth,
    },
  },
  badgePadding: {
    padding: theme.spacing(0, 2),
  },
  marginFloatButton: {
    position: "fixed",
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  },
  notificationButton: {
    position: "fixed",
    bottom: theme.spacing(2),
    right: theme.spacing(8),
  },
}));

// Detect iOS Safari (excluding Chrome, Firefox, and other browsers on iOS)
const isIOSSafari = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  
  const userAgent = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(userAgent);
  const isWebKit = /WebKit/.test(userAgent);
  
  // Check if it's iOS but NOT Chrome, Firefox, or other third-party browsers
  // Safari on iOS doesn't have 'CriOS' (Chrome), 'FxiOS' (Firefox), etc.
  const isNotChrome = !/CriOS/.test(userAgent);
  const isNotFirefox = !/FxiOS/.test(userAgent);
  const isNotEdge = !/EdgiOS/.test(userAgent);
  const isNotOtherBrowser = !/OPiOS/.test(userAgent); // Opera
  
  return isIOS && isWebKit && isNotChrome && isNotFirefox && isNotEdge && isNotOtherBrowser;
};

// Check if FCM Web Push is actually supported
const isFCMSupported = (firebase) => {
  // iOS Safari never supports FCM
  if (isIOSSafari()) {
    return false;
  }
  
  // Check if Firebase messaging is initialized
  if (!firebase || !firebase.messaging) {
    return false;
  }
  
  // Check if Notification API is available
  if (!('Notification' in window)) {
    return false;
  }
  
  // Check if Service Worker is supported (required for FCM)
  if (!('serviceWorker' in navigator)) {
    return false;
  }
  
  // Check if PushManager is available (required for Web Push)
  if (!('PushManager' in window)) {
    return false;
  }
  
  return true;
};

// Play notification sound
const playNotificationSound = () => {
  try {
    const audio = new Audio('/notification-sound.mp3');
    audio.volume = 0.5;
    audio.play().catch(e => console.log('Could not play sound:', e));
  } catch (e) {
    console.log('Audio not supported');
  }
};

function CheckoutHome({ firebase }) {
  const classes = useStyles();
  const open = true;
  const context = useContext(AuthUserContext);
  const authUser = context?.authUser !== undefined ? context.authUser : context;
  const [notificationsEnabled, setNotificationsEnabled] = React.useState(false);
  // Initialize messagingSupported based on iOS Safari detection
  const [messagingSupported, setMessagingSupported] = React.useState(() => {
    // Don't show on iOS Safari from the start
    if (isIOSSafari()) {
      return false;
    }
    return true;
  });
  const unsubscribeRef = React.useRef(null);

  // Setup FCM notifications
  const setupNotifications = useCallback(async () => {
    if (!authUser || !authUser.default_organization || !firebase) return;

    try {
      // Explicitly check for iOS Safari first
      if (isIOSSafari()) {
        console.log('iOS Safari detected - FCM not supported');
        setMessagingSupported(false);
        NotificationManager.warning(
          'Notificações push não são suportadas neste navegador. Use Chrome, Firefox ou Edge para receber notificações.',
          'Navegador Incompatível',
          5000
        );
        return;
      }

      // Use comprehensive FCM support check
      if (!isFCMSupported(firebase)) {
        console.log('FCM not supported in this browser');
        setMessagingSupported(false);
        NotificationManager.warning(
          'Notificações push não são suportadas neste navegador. Use Chrome, Firefox ou Edge para receber notificações.',
          'Navegador Incompatível',
          5000
        );
        return;
      }

      const token = await firebase.requestNotificationPermission();
      if (token) {
        setNotificationsEnabled(true);
        setMessagingSupported(true);
        // Support both direct uid and _delegate.uid (Firebase v9+ compat mode)
        const uid = authUser?.uid || authUser?._delegate?.uid;
        await firebase.saveFcmToken(authUser.default_organization, token, uid);
        NotificationManager.success('Notificações ativadas com sucesso!', 'Notificações', 3000);
        
        // Clean up previous listener if exists
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
        }
        
        // Listen for foreground messages
        const unsubscribe = firebase.onForegroundMessage((payload) => {
          console.log('Foreground message received:', payload);
          playNotificationSound();
          const notificationBody = (payload.notification && payload.notification.body) || 'Um novo pedido foi recebido';
          const notificationTitle = (payload.notification && payload.notification.title) || '🔔 Novo Pedido!';
          NotificationManager.info(
            notificationBody,
            notificationTitle,
            10000
          );
        });
        
        if (unsubscribe) {
          unsubscribeRef.current = unsubscribe;
        }
      } else {
        setNotificationsEnabled(false);
        NotificationManager.warning('Permissão de notificações negada', 'Notificações', 3000);
      }
    } catch (error) {
      console.error('Error setting up notifications:', error);
      setNotificationsEnabled(false);
      setMessagingSupported(false);
    }
  }, [authUser, firebase]);

  // Check if notifications are already enabled
  useEffect(() => {
    // Skip notification setup on iOS Safari
    if (isIOSSafari()) {
      setMessagingSupported(false);
      return;
    }
    
    if (typeof window !== 'undefined' && 'Notification' in window && 
        Notification.permission === 'granted' && authUser && authUser.default_organization) {
      setupNotifications();
    }
    
    // Check if FCM is supported
    if (firebase && !isFCMSupported(firebase)) {
      setMessagingSupported(false);
    }
    
    // Cleanup listener on unmount
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [authUser, setupNotifications, firebase]);

  useEffect(() => {
    ProductRepository.setOrderRepositoryFirebase(firebase, authUser);
    OrderRepository.setOrderRepositoryFirebase(firebase, authUser);

    // Only sync if authUser is fully loaded with uid
    // Support both direct uid and _delegate.uid (Firebase v9+ compat mode)
    const uid = authUser?.uid || authUser?._delegate?.uid;
    const orgId = authUser?.default_organization;
    
    if (authUser && uid && orgId) {
      ProductRepository.syncProducts();
      OrderRepository.syncOrders();
      OrderRepository.syncClientLastOrders();
      
      // Ativar listeners em tempo real
      OrderRepository.enableRealtimeOrdersQueue();
      OrderRepository.enableRealtimeOrdersShipped();
    }
    
    // Cleanup ao desmontar
    return () => {
      OrderRepository.disableRealtimeOrders();
    };
  }, [firebase, authUser]);

  const connectedSymbol = () => {
    return (
      <Box zIndex="tooltip">
        <NetworkLockedIcon
          size="small"
          style={{ color: `${authUser ? "green" : "gray"}` }}
          className={classes.marginFloatButton}
        />
      </Box>
    );
  };

  const notificationButton = () => {
    if (!authUser) return null;
    
    // Don't show button if messaging is not supported
    if (!messagingSupported) {
      return null;
    }
    
    return (
      <Box zIndex="tooltip" className={classes.notificationButton}>
        <Tooltip title={notificationsEnabled ? "Notificações ativadas" : "Ativar notificações de novos pedidos"}>
          <IconButton
            onClick={setupNotifications}
            style={{ 
              color: notificationsEnabled ? "green" : "orange",
              backgroundColor: 'white',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}
          >
            {notificationsEnabled ? <NotificationsActiveIcon /> : <NotificationsOffIcon />}
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  return (
    <div className={classes.root}>
      {connectedSymbol()}
      {notificationButton()}

      <OrderQueue />

      <main
        className={clsx(classes.content, {
          [classes.contentShift]: open,
        })}
      >
        <Checkout />
      </main>
    </div>
  );
}

export default withFirebase(CheckoutHome);
